// Translates API error messages (FastAPI "detail") into the UI language.
// The backend speaks a mix of English and Russian; the dictionaries map
// - English server text -> Russian (i18n/server.ru.json)
// - Russian server text -> English (i18n/server.en.json)
// Keys may contain {0}, {1}... for the dynamic parts of f-string messages, and
// a key ending in ": " also matches anything after it (an appended reason).
import RU from "../i18n/server.ru.json";
import EN from "../i18n/server.en.json";
import { getTxLang } from "./tx";

const escape = (s) => s.replace(/[.*+?^$()|[\]\\]/g, "\\$&");

function compile(dict) {
  const exact = new Map();
  const patterns = [];
  for (const [key, value] of Object.entries(dict)) {
    const dynamic = /\{\d+\}/.test(key) || /:\s$/.test(key);
    if (!dynamic) {
      exact.set(key, value);
      continue;
    }
    const order = [];
    let body = escape(key).replace(/\\\{(\d+)\\\}|\{(\d+)\}/g, (_m, a, b) => {
      order.push(Number(a ?? b));
      return "([\\s\\S]*?)";
    });
    let tail = false;
    if (/:\s$/.test(key)) {
      body += "([\\s\\S]*)";
      tail = true;
    }
    patterns.push({ re: new RegExp("^" + body + "$"), order, value, tail, size: key.length });
  }
  // most specific (longest) templates first
  patterns.sort((a, b) => b.size - a.size);
  return { exact, patterns };
}

const TABLES = { ru: compile(RU), en: compile(EN) };

export function translateServerMessage(message, lang = getTxLang()) {
  if (typeof message !== "string" || !message) return message;
  const table = TABLES[lang === "ru" ? "ru" : "en"];
  const trimmed = message.trim();
  if (table.exact.has(trimmed)) return table.exact.get(trimmed);
  for (const p of table.patterns) {
    const m = trimmed.match(p.re);
    if (!m) continue;
    const groups = m.slice(1);
    const args = {};
    p.order.forEach((n, i) => {
      args[n] = groups[i];
    });
    let out = p.value.replace(/\{(\d+)\}/g, (s, n) => (args[n] !== undefined ? args[n] : s));
    if (p.tail) out += groups[groups.length - 1];
    return out;
  }
  return message;
}

// Field names FastAPI reports in validation errors -> UI labels.
const FIELD_LABELS = {
  anchor_texts: { en: "Anchor texts", ru: "Анкоры" },
  keywords: { en: "Keywords", ru: "Ключевые слова" },
  satellite_ids: { en: "Satellites", ru: "Сателлиты" },
  target_site_id: { en: "Target site", ru: "Целевой сайт" },
  target_urls: { en: "Target URLs", ru: "Целевые URL" },
  articles_per_satellite: { en: "Articles per satellite", ru: "Статей на сателлит" },
  link_density: { en: "Links per article", ru: "Ссылок на статью" },
  publish_cadence: { en: "Publish cadence", ru: "Темп публикации" },
  name: { en: "Name", ru: "Название" },
  url: { en: "URL", ru: "URL" },
  email: { en: "Email", ru: "Email" },
  password: { en: "Password", ru: "Пароль" },
};

function fieldLabel(loc, lang) {
  const parts = (Array.isArray(loc) ? loc : []).filter((p) => p !== "body" && p !== "query" && p !== "path");
  const name = [...parts].reverse().find((p) => typeof p === "string");
  if (!name) return "";
  const known = FIELD_LABELS[name];
  return known ? known[lang] : name;
}

function formatOne(err, lang) {
  const ru = lang === "ru";
  const field = fieldLabel(err.loc, lang);
  const ctx = err.ctx || {};
  let text;
  switch (err.type) {
    case "too_short":
      text = ru
        ? "нужно минимум " + ctx.min_length + ", сейчас " + ctx.actual_length
        : "at least " + ctx.min_length + " required, got " + ctx.actual_length;
      break;
    case "too_long":
      text = ru ? "не больше " + ctx.max_length : "at most " + ctx.max_length + " allowed";
      break;
    case "missing":
      text = ru ? "обязательное поле" : "required";
      break;
    case "greater_than_equal":
      text = ru ? "должно быть не меньше " + ctx.ge : "must be at least " + ctx.ge;
      break;
    case "less_than_equal":
      text = ru ? "должно быть не больше " + ctx.le : "must be at most " + ctx.le;
      break;
    case "string_too_short":
      text = ru ? "слишком короткое значение" : "too short";
      break;
    default: {
      const msg = String(err.msg || "").replace(/^Value error,\s*/, "");
      text = translateServerMessage(msg, lang);
    }
  }
  return field ? field + ": " + text : text;
}

/**
 * Turn a non-string FastAPI "detail" into one readable line. A 422 carries a
 * list of pydantic errors and some endpoints return an object; handing either
 * to toast() crashed the whole page ("Objects are not valid as a React child").
 */
export function formatErrorDetail(detail, lang = getTxLang()) {
  if (typeof detail === "string") return translateServerMessage(detail, lang);
  if (Array.isArray(detail)) {
    const parts = detail.map((e) => (e && typeof e === "object" ? formatOne(e, lang) : String(e))).filter(Boolean);
    return parts.join("; ");
  }
  if (detail && typeof detail === "object") {
    const msg = detail.message || detail.error || detail.detail;
    if (typeof msg === "string") return translateServerMessage(msg, lang);
    return JSON.stringify(detail);
  }
  return detail == null ? detail : String(detail);
}

/** axios error interceptor helper: make error.response.data.detail a translated string */
export function translateErrorDetail(error) {
  const data = error && error.response && error.response.data;
  if (!data || data.detail == null) return error;
  if (typeof data.detail === "string") {
    data.detail = translateServerMessage(data.detail);
  } else {
    // keep the structured original for code that wants it
    data.detail_raw = data.detail;
    data.detail = formatErrorDetail(data.detail);
  }
  return error;
}
